import { useEffect, useMemo, useState } from 'react'
import { ArrowRight, Inbox, PackagePlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  isTruncated,
  keptAtFilterText,
  NO_KEPT_AT_MESSAGE,
  splitByKeptAt,
  TRUNCATED_MESSAGE,
  UNSTATED_LABEL,
  usableKeptAt,
} from '@/lib/putAwayFilter'
import { useSetLotLocation, useStorageConditions, useUnplacedLots } from '@/hooks/useStorage'
import { AddLotModal } from '@/components/inventory/AddLotModal'
import { KeptAtBadge } from './KeptAtBadge'
import { MaterialPickerDialog } from './MaterialPickerDialog'
import type { ApiMaterial } from '@/types/inventory'
import type { ApiStorageNode, StorageCondition, UnplacedLot } from '@/types/storage'
import { useLocationText } from '@/hooks/useStoragePlaces'

/**
 * Put stock into a chosen slot, by either of the two ways stock gets there.
 *
 * **Move something already received.** The lot is chosen from a searchable
 * dropdown rather than a scrolling list of buttons: a facility's put-away queue
 * is hundreds of lots long, and a list of that many "Place" buttons is both slow
 * to scan and easy to misclick. Sourced from every unplaced lot, not just the
 * migration's holding area — which most environments do not have, so ordinary
 * received stock previously had no route into the hierarchy at all. When the
 * destination declares a storage condition the queue is filtered to what that
 * condition can legally take, so the wrong lot cannot be selected at all.
 *
 * **Receive new stock straight into it.** Stock usually arrives at a shelf, not
 * at a queue. Without this the only route was to leave the map, receive it with
 * no location, come back and find it again — and a slot whose queue was empty
 * was a dead end that explained where stock comes from instead of accepting any.
 */
export function PutAwayPanel({
  slot,
  slotCondition,
  onDone,
  onCancel,
}: {
  slot: ApiStorageNode
  /**
   * The Kept at in force at the slot, from its whole ancestor chain
   * (`destinationKeptAt`). Null means none is set anywhere above it, and then
   * nothing is offered: an unfiltered queue is every lot in the tenant.
   */
  slotCondition: StorageCondition | null
  onDone: () => void
  onCancel: () => void
}) {
  const locate = useLocationText()
  const [lotId, setLotId] = useState('')
  // Receiving is a two-step flow: which material, then how much of it.
  const [picking, setPicking] = useState(false)
  const [material, setMaterial] = useState<ApiMaterial | null>(null)
  const [showUnstated, setShowUnstated] = useState(false)
  const setLotLocation = useSetLotLocation()
  const conditions = useStorageConditions()
  const conditionMeta = conditions.data?.conditions ?? []
  // A non-persistent condition (a controlled-rate freezer) is not somewhere
  // stock rests — the server refuses put-away there — so it counts as none.
  const keptAt = usableKeptAt(slotCondition, conditionMeta)
  // Two reads, because `requires=` matches EXACTLY and so leaves out lots whose
  // material states no temperature — legal anywhere, and shown apart. The
  // unfiltered read is only for those; anything needing another temperature
  // is dropped from it and never offered.
  const exact = useUnplacedLots(keptAt, keptAt != null)
  const all = useUnplacedLots(null, keptAt != null)
  const loading = exact.isLoading || all.isLoading

  const { matching, unstated } = useMemo(() => {
    if (!keptAt) return { matching: [] as UnplacedLot[], unstated: [] as UnplacedLot[] }
    return {
      matching: splitByKeptAt(exact.data ?? [], keptAt).matching,
      unstated: splitByKeptAt(all.data ?? [], keptAt).unstated,
    }
  }, [exact.data, all.data, keptAt])
  // Either read at the server's cap may be missing lots beyond it.
  const truncated = isTruncated(exact.data) || isTruncated(all.data)
  const filterLine = keptAt ? (
    <p className='text-xs text-muted-foreground'>{keptAtFilterText(keptAt, conditionMeta)}</p>
  ) : null

  /** Collapsing the unstated group must not leave one of its lots chosen. */
  const toggleUnstated = () => {
    if (showUnstated && unstated.some((l) => l.lotId === lotId)) setLotId('')
    setShowUnstated((v) => !v)
  }
  const lots = useMemo(() => [...matching, ...unstated], [matching, unstated])
  const chosen = lots.find((l) => l.lotId === lotId) ?? null

  // Someone else may put the selected lot away first. Clearing a selection that
  // no longer exists is what stops the button submitting a stale id.
  useEffect(() => {
    if (lotId && !loading && !lots.some((l) => l.lotId === lotId)) setLotId('')
  }, [lots, lotId, loading])

  return (
    <div className='space-y-3 rounded-lg border bg-card p-3'>
      <div className='flex items-start justify-between gap-3'>
        <div className='min-w-0'>
          <p className='flex items-center gap-2 text-sm font-medium'>
            <PackagePlus className='h-4 w-4 text-muted-foreground' />
            Put stock into {slot.code}
          </p>
          <p className='mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground'>
            {locate(slot.path)}
            <KeptAtBadge condition={slotCondition} />
          </p>
        </div>
        <Button variant='ghost' size='sm' onClick={onCancel}>
          Cancel
        </Button>
      </div>

      {keptAt == null ? (
        <p className='rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900'>
          {NO_KEPT_AT_MESSAGE}
        </p>
      ) : loading ? (
        <p className='py-6 text-center text-xs text-muted-foreground'>Loading stock…</p>
      ) : lots.length === 0 ? (
        <>
          {filterLine}
          <EmptyQueue condition={keptAt} onReceive={() => setPicking(true)} />
          {truncated && <p className='text-xs text-muted-foreground'>{TRUNCATED_MESSAGE}</p>}
        </>
      ) : (
        <>
          <div className='space-y-1'>
            <Label>Which lot</Label>
            {filterLine}
            <Select value={lotId} onValueChange={setLotId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    matching.length > 0
                      ? `Choose from ${matching.length} waiting…`
                      : 'None waiting at this temperature'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {matching.map((lot) => (
                  <LotOption key={lot.lotId} lot={lot} />
                ))}
                {/* Collapsed by default: these may be fine here, but nothing
                    in their master data says so. */}
                {showUnstated && unstated.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>{UNSTATED_LABEL}</SelectLabel>
                    {unstated.map((lot) => (
                      <LotOption key={lot.lotId} lot={lot} />
                    ))}
                  </SelectGroup>
                )}
                {/* A Select needs at least one item to open on. */}
                {matching.length === 0 && !showUnstated && (
                  <SelectItem value='__none' disabled>
                    None waiting at this temperature
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {unstated.length > 0 && (
              <button
                type='button'
                aria-expanded={showUnstated}
                onClick={toggleUnstated}
                className='text-xs font-medium text-primary hover:underline'
              >
                {showUnstated ? 'Hide' : 'Show'} {unstated.length} with {UNSTATED_LABEL.toLowerCase()}
              </button>
            )}
            {truncated && <p className='text-xs text-muted-foreground'>{TRUNCATED_MESSAGE}</p>}
          </div>

          {chosen && <ChosenLot lot={chosen} slot={slot} />}

          <div className='flex flex-wrap items-center justify-between gap-2'>
            {/* The queue is not the only source — stock can arrive straight
                at this shelf. Secondary, because placing what is already
                waiting is the commoner action when the queue has anything. */}
            <Button variant='outline' size='sm' className='gap-1.5' onClick={() => setPicking(true)}>
              <PackagePlus className='h-3.5 w-3.5' />
              Receive new stock
            </Button>
            <Button
              size='sm'
              className='gap-1.5'
              disabled={!chosen || setLotLocation.isPending}
              onClick={() =>
                chosen &&
                setLotLocation.mutate(
                  { lotId: chosen.lotId, storageNodeId: slot.id },
                  { onSuccess: onDone },
                )
              }
            >
              {setLotLocation.isPending ? 'Placing…' : 'Place here'}
              <ArrowRight className='h-3.5 w-3.5' />
            </Button>
          </div>
        </>
      )}

      {setLotLocation.error && (
        <p className='text-sm text-destructive'>{String(setLotLocation.error.message)}</p>
      )}

      <MaterialPickerDialog
        open={picking}
        onClose={() => setPicking(false)}
        onPick={(m) => {
          setPicking(false)
          setMaterial(m)
        }}
        description={`Pick what arrived. It goes straight into ${locate(slot.path)}.`}
        // The slot is already fixed here — z8nrz7dyd2 AC6 — so it is the
        // material list that narrows, not a unit picker further down the form.
        destinationCondition={keptAt}
      />

      {/* The slot is passed, so the modal states the destination instead of
          asking for one — and the backend checks the material against what
          this location provides before it is accepted. */}
      <AddLotModal
        materialId={material?.id ?? null}
        materialLabel={material ? `${material.materialId} · ${material.materialName}` : undefined}
        requiresCondition={material?.requiresCondition ?? null}
        slot={slot}
        open={material !== null}
        onClose={() => setMaterial(null)}
        // Only on an actual receipt: the slot is no longer free, so the
        // put-away form it was opened from has nothing left to say.
        onSaved={onDone}
      />
    </div>
  )
}

function LotOption({ lot }: { lot: UnplacedLot }) {
  return (
    <SelectItem value={lot.lotId}>
      {lot.lotNumber} · {lot.materialCode} {lot.materialName} · {lot.quantity}
    </SelectItem>
  )
}

/** What is about to move, and where — read back before committing. */
function ChosenLot({ lot, slot }: { lot: UnplacedLot; slot: ApiStorageNode }) {
  const locate = useLocationText()
  return (
    <div className='rounded-md border bg-muted/30 px-3 py-2'>
      <div className='flex flex-wrap items-baseline gap-2 text-sm'>
        <span className='font-medium'>{lot.lotNumber}</span>
        <Badge variant='outline' className='text-[10px] font-normal'>
          {lot.quantity}
        </Badge>
        {lot.requiresCondition && <KeptAtBadge condition={lot.requiresCondition} />}
      </div>
      <p className='mt-0.5 truncate text-xs text-muted-foreground'>
        {lot.materialCode} · {lot.materialName}
        {lot.expiryDate && ` · expires ${lot.expiryDate.slice(0, 10)}`}
      </p>
      <p className='mt-1.5 flex items-center gap-1.5 text-xs'>
        <ArrowRight className='h-3 w-3 text-muted-foreground' />
        {locate(slot.path)}
      </p>
    </div>
  )
}

/**
 * Nothing is waiting — which is a normal state, not a failure, and is why the
 * receive action is the primary one here rather than a footnote about where
 * stock comes from.
 */
function EmptyQueue({
  condition,
  onReceive,
}: {
  condition: StorageCondition | null
  onReceive: () => void
}) {
  return (
    <div className='flex flex-col items-center gap-2 py-6 text-center'>
      <Inbox className='h-5 w-5 text-muted-foreground' />
      <p className='text-xs text-muted-foreground'>
        {condition
          ? 'No stock is waiting that needs this Kept at.'
          : 'Nothing is waiting to be put away.'}
      </p>
      <Button size='sm' className='mt-0.5 gap-1.5' onClick={onReceive}>
        <PackagePlus className='h-3.5 w-3.5' />
        Receive new stock here
      </Button>
      <p className='max-w-xs text-[11px] text-muted-foreground'>
        Or receive it with no location, and it will appear in this queue for whoever shelves it.
      </p>
    </div>
  )
}
